import api from 'configs/api'
import { getUserAuthenticationConversationHeader } from 'utils/conversationUserAuthHeader'
import type { ConversationAuthPair } from 'utils/conversationUserAuthHeader'
import { toConversationsApiError } from './conversationsApiError'

/** Prefixo do serviço no gateway. Todas as rotas do conversations-ms saem daqui. */
const SERVICE_PREFIX = '/conversations/v1'
const BASE_URL = `${SERVICE_PREFIX}/conversations`

/**
 * Ponto único de conversão do erro. Sem isto, um 503 de quota e um 400 de
 * payload chegam na UI como o mesmo `Error` genérico, e a única reação possível
 * é retentar às cegas — que é a origem do incidente do Twilio 50214.
 */
async function request<T>(run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const response = await run()
    return response.data
  } catch (err) {
    throw toConversationsApiError(err)
  }
}

export interface CreateConversationBody {
  personId: string
  professionalId: string
}

/**
 * Único ponto que decide "esta conversa está encerrada?".
 *
 * Existe para que ninguém decida por `status` — a armadilha nº 1 do guia.
 */
export const estaEncerrada = (c: { closedAt?: string | null }): boolean =>
  c.closedAt != null

export interface ConversationResponse {
  id: string
  twilioConversationSid: string
  personId: string
  professionalId: string
  status: string
  participants: Array<{
    identity: string
    sid: string
  }>
  /**
   * Fase 5 — **fonte da verdade do encerramento**.
   *
   * ⚠️ Nunca decidir por `status`: conversa encerrada tem `status: 'ACTIVE'`
   * com `closedAt` preenchido. `status` só vira `ARCHIVED` quando o Twilio
   * fecha a conversa, o que encerrar **não** faz.
   */
  closedAt?: string | null
  closedBy?: 'PATIENT' | 'PROFESSIONAL' | 'SYSTEM' | null
  closeReason?: string | null
}

export interface TokenResponse {
  token: string
  identity: string
  expiresIn: number
}

/**
 * POST /conversations/v1/conversations/token
 * Gera JWT Access Token para SDK Twilio
 */
export async function postConversationsToken(
  twilioAs: 'patient' | 'professional',
  authPair: ConversationAuthPair
): Promise<TokenResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request(() =>
    api.post(`${BASE_URL}/token?twilio_as=${twilioAs}`, null, {
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )
}

/**
 * POST /conversations/v1/conversations/token/refresh
 * Renova token JWT existente
 */
export async function postConversationsTokenRefresh(
  twilioAs: 'patient' | 'professional',
  authPair: ConversationAuthPair
): Promise<TokenResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  // Faltava o prefixo `/conversations/v1` que as outras cinco rotas usam: o path
  // antigo (`v1/twilio/token/refresh`) não era roteado para o serviço e
  // respondia 404 no gateway, então o token nunca renovava e a sessão morria em
  // 1h com `20104 Access Token expired`. Confirmado contra a collection do
  // Twilio MS. O `params: { twilioAs }` era um segundo parâmetro, redundante com
  // o `twilio_as` já presente na query.
  return request<TokenResponse>(() =>
    api.post(
      `${SERVICE_PREFIX}/twilio/token/refresh?twilio_as=${twilioAs}`,
      null,
      {
        headers: {
          UserAuthenticationConversation: authHeader,
        },
      }
    )
  )
}

/**
 * Ensure em voo por par. Ao clicar em "Iniciar atendimento",
 * `prepararConversaDoPar` e o `initializeChat` do hook chamam
 * `postConversations` quase juntos; sem a conversa no banco, as duas correm no
 * conversations-ms (Twilio 409 + `P2002` no INSERT). Compartilhar a promise
 * evita o POST duplicado.
 *
 * A chave usa só o `body`: `authPair` deve ser o mesmo par do `body`, e o
 * objeto/erro retornado é compartilhado entre os chamadores (não mutar).
 *
 * shortcut: dedupe só dentro da mesma aba; corrida entre abas/dispositivos
 * exige ensure idempotente no conversations-ms.
 */
const ensureEmVoo = new Map<string, Promise<ConversationResponse>>()

/**
 * POST /conversations/v1/conversations
 * Cria/garante conversa (idempotente)
 */
export function postConversations(
  body: CreateConversationBody,
  authPair: ConversationAuthPair
): Promise<ConversationResponse> {
  const chave = `${body.personId}:${body.professionalId}`
  const existente = ensureEmVoo.get(chave)
  if (existente) return existente

  const promessa = (async () => {
    const authHeader = await getUserAuthenticationConversationHeader(authPair)

    return request<ConversationResponse>(() =>
      api.post(`${BASE_URL}`, body, {
        headers: {
          UserAuthenticationConversation: authHeader,
        },
      })
    )
  })().finally(() => {
    ensureEmVoo.delete(chave)
  })

  ensureEmVoo.set(chave, promessa)
  return promessa
}

/**
 * GET /conversations/v1/conversations
 * Lista conversas
 */
export async function getConversationsList(
  authPair: ConversationAuthPair,
  query?: { person_id?: string; professional_id?: string }
): Promise<ConversationResponse[]> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  const resposta = await request<unknown>(() =>
    api.get(`${BASE_URL}`, {
      params: query,
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )

  /**
   * ⚠️ A resposta **não é sempre uma lista**.
   *
   * Filtrando por `person_id` + `professional_id`, o backend devolve **o
   * objeto** da conversa daquele par — a conversa é única por par. Sem filtro,
   * devolve array.
   *
   * Todos os chamadores faziam `.find()` direto e falhavam em silêncio no
   * primeiro caso: nenhum erro, só um resultado vazio. Normalizar aqui evita
   * que cada um descubra isso por conta própria.
   */
  if (Array.isArray(resposta)) return resposta as ConversationResponse[]
  if (resposta && typeof resposta === 'object' && 'id' in resposta) {
    return [resposta as ConversationResponse]
  }
  return []
}

/**
 * GET /conversations/v1/conversations/:id
 * Detalhe de uma conversa
 */
export async function getConversationById(
  id: string,
  authPair: ConversationAuthPair
): Promise<ConversationResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request(() =>
    api.get(`${BASE_URL}/${id}`, {
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )
}

/**
 * DELETE /conversations/v1/conversations/:sid
 * Remove conversa pelo twilioConversationSid
 */
export async function deleteConversation(
  sid: string,
  authPair: ConversationAuthPair
): Promise<void> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  await request(() =>
    api.delete(`${BASE_URL}/${sid}`, {
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )
}

// ---------------------------------------------------------------------------
// Histórico pela API (Fase 2 — shadow read)
// ---------------------------------------------------------------------------

/** Anexo do `/history`. `url` é **relativa ao serviço**: exige o prefixo do gateway. */
export interface HistoryMedia {
  id?: string
  twilioMediaSid?: string
  filename?: string
  contentType?: string
  sizeBytes?: number
  url?: string
}

export interface HistoryAuthor {
  /** Campo declarado para autoria. Preferir a `identity` (§4.2 da spec). */
  role?: 'PATIENT' | 'PROFESSIONAL' | 'SYSTEM' | 'UNKNOWN'
  identity?: string
}

export interface HistoryMessage {
  messageSid: string
  index?: number
  conversationSid?: string
  author?: HistoryAuthor
  body?: string | null
  media?: HistoryMedia[]
  createdAt?: string
  editedAt?: string | null
}

export interface HistoryPage {
  items: HistoryMessage[]
  nextCursor: string | null
  unread?: { professional?: number; patient?: number }
  /**
   * ⚠️ O contrato não documenta o significado deste booleano. A leitura provável
   * é "backfill daquele par concluído", mas isso não foi confirmado com o
   * backend — não usar como portão sem confirmar.
   */
  complete?: boolean
}

export interface HistoryQuery {
  personId: string
  professionalId: string
  cursor?: string
  /** 1–100; fora da faixa o servidor clampa, não rejeita. */
  limit?: number
  /** `desc` traz as mais recentes primeiro — a janela que o SDK abre. */
  order?: 'asc' | 'desc'
}

/**
 * `GET /conversations/v1/conversations/history`
 *
 * Só o que o shadow read precisa é tipado: `messageSid` e `nextCursor`. Os
 * demais campos do contrato (`unread`, `complete`, `media[]`) entram na Fase 3,
 * quando esta virar a fonte de leitura — declarar agora seria fixar uma forma
 * que ainda não foi vista respondendo com dado real.
 *
 * ⚠️ Par sem conversa devolve **404**, não `items: []`. Quem chama trata como
 * "sem histórico", não como erro — confirmado contra staging em 06/08/2026.
 */
export async function getConversationsHistory(
  query: HistoryQuery,
  authPair: ConversationAuthPair
): Promise<HistoryPage> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request<HistoryPage>(() =>
    api.get(`${BASE_URL}/history`, {
      params: {
        person_id: query.personId,
        professional_id: query.professionalId,
        limit: query.limit ?? 100,
        ...(query.order ? { order: query.order } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
      },
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )
}

/**
 * Fase 3 — marca a conversa como lida pelo **UUID interno** da conversa (não o
 * `conversationSid` do Twilio). Substitui `advanceLastReadMessageIndex` quando
 * o `/history` é a fonte de leitura.
 */
export async function markConversationRead(
  conversationId: string,
  authPair: ConversationAuthPair
): Promise<void> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  await request<unknown>(() =>
    // `{}` e não `null`: com `null` o axios não manda `Content-Type: json` e o
    // backend reprovou com 400. O guia usa `{}` no `reopen` pelo mesmo motivo.
    api.post(
      `${BASE_URL}/${conversationId}/read`,
      {},
      {
        headers: {
          UserAuthenticationConversation: authHeader,
        },
      }
    )
  )
}

/** Resposta do `presence/ensure` (Fase 4). */
export interface PresenceEnsureResponse {
  professionalPresence?: 'ACTIVE' | 'INACTIVE' | string
  professionalDesired?: string
  professionalParticipantSid?: string
  presenceReconciledAt?: string
  presenceLastError?: string | null
}

/**
 * Fase 4 — garante a participação do profissional na conversa.
 *
 * Passo obrigatório antes de pedir token quando a presença é gerida pelo
 * backend. `503 PROFESSIONAL_CONVERSATION_LIMIT` não é erro de tela: é o
 * servidor dizendo que não há vaga agora, e a conversa segue **legível**.
 */
export async function postPresenceEnsure(
  conversationId: string,
  authPair: ConversationAuthPair
): Promise<PresenceEnsureResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request<PresenceEnsureResponse>(() =>
    // Mesmo motivo do `/read`: corpo vazio como objeto, não `null`.
    api.post(
      `${BASE_URL}/${conversationId}/presence/ensure`,
      {},
      {
        headers: {
          UserAuthenticationConversation: authHeader,
        },
      }
    )
  )
}

/** Corpo do `close`. Ambos opcionais — ver Fase 5 da spec. */
export interface CloseConversationBody {
  closedBy?: 'patient' | 'professional'
  /**
   * Rótulo curto de lista fixa. **Vai para log de auditoria**: proibido
   * sintoma, diagnóstico, medicação, nome, telefone ou qualquer dado pessoal.
   */
  reason?: string
}

/**
 * Fase 5 — encerra a conversa. Remove a participação **dos dois lados** no
 * Twilio; o histórico continua servido pelo `/history`, que lê o banco.
 *
 * Idempotente: chamar duas vezes devolve o mesmo `closedAt`.
 */
export async function postConversationClose(
  conversationId: string,
  authPair: ConversationAuthPair,
  body: CloseConversationBody = {}
): Promise<ConversationResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request<ConversationResponse>(() =>
    api.post(`${BASE_URL}/${conversationId}/close`, body, {
      headers: {
        UserAuthenticationConversation: authHeader,
      },
    })
  )
}

/**
 * Fase 5 — reabre a conversa.
 *
 * ⚠️ Readmite o **paciente**, não o profissional: a presença dele é por
 * demanda. O cliente do profissional precisa chamar `presence/ensure` **depois**
 * — sem isso o SDK dele continua em 403.
 *
 * Idempotente: conversa já aberta devolve 200 sem tocar o Twilio.
 */
export async function postConversationReopen(
  conversationId: string,
  authPair: ConversationAuthPair
): Promise<ConversationResponse> {
  const authHeader = await getUserAuthenticationConversationHeader(authPair)

  return request<ConversationResponse>(() =>
    api.post(
      `${BASE_URL}/${conversationId}/reopen`,
      {},
      {
        headers: {
          UserAuthenticationConversation: authHeader,
        },
      }
    )
  )
}
